import OrbitAPIClient from "@withorbit/api-client";
import {
  AttachmentID,
  ColorPaletteName,
  createReviewQueue,
  EntityType,
  Event,
  EventType,
  generateUniqueID,
  getReviewQueueFuzzyDueTimestampThreshold,
  ReviewItem,
  Task,
  TaskID,
  TaskIngestEvent,
  TaskProvenance,
  TaskSpec,
  TaskUpdateDeletedEvent,
  TaskUpdateProvenanceEvent,
  TaskUpdateSpecEvent,
} from "@withorbit/core";
import { OrbitStore } from "@withorbit/store-shared";
import { APISyncAdapter, syncOrbitStore } from "@withorbit/sync";
import { createDefaultOrbitStore } from "./orbitStoreFactory.js";
import {
  buildCollectionSummaries,
  CollectionID,
  CollectionSummary,
  getCollectionIDForTask,
  makeOrphansProvenance,
} from "./collections.js";

export class DatabaseManager {
  private readonly _storePromise: Promise<OrbitStore>;

  private readonly _apiClient: OrbitAPIClient;
  private readonly _apiSyncAdapter: APISyncAdapter;
  private _syncPromise: Promise<void> | null = null;

  constructor(apiClient: OrbitAPIClient) {
    this._apiClient = apiClient;
    this._storePromise = createDefaultOrbitStore(); // TODO: namespace the store by user ID
    this._apiSyncAdapter = new APISyncAdapter(this._apiClient, "server");

    this._startSync();
  }

  async fetchReviewQueue(options?: {
    collectionID?: CollectionID;
  }): Promise<ReviewItem[]> {
    // TODO: implement an approach more resilient to network failures... probably sync status should be separately exported and displayed.
    await this._syncPromise;

    const store = await this._storePromise;
    const thresholdTimestampMillis = getReviewQueueFuzzyDueTimestampThreshold();

    const dueTasks = await store.database.listEntities<Task>({
      entityType: EntityType.Task,
      limit: 500,
      predicate: ["dueTimestampMillis", "<=", thresholdTimestampMillis],
    });

    // Filter out deleted tasks
    let filteredTasks = dueTasks.filter((task) => !task.isDeleted);

    // Filter by collection if specified
    if (options?.collectionID) {
      filteredTasks = filteredTasks.filter(
        (task) => getCollectionIDForTask(task) === options.collectionID,
      );
    }

    return createReviewQueue(filteredTasks);
  }

  async close(): Promise<void> {
    // TODO: cancel sync... which requires implementing cancellation in @withorbit/sync...

    const store = await this._storePromise;
    await store.database.close();
  }

  async recordEvents(events: Event[]): Promise<void> {
    console.log("[DatabaseManager] recordEvents called with", events.length, "events");
    console.log("[DatabaseManager] Event types:", events.map(e => e.type).join(", "));
    console.log("[DatabaseManager] Event entityIDs:", events.map(e => e.entityID).join(", "));

    // Log provenance for TaskIngest events
    for (const event of events) {
      if (event.type === EventType.TaskIngest) {
        const ingestEvent = event as TaskIngestEvent;
        console.log("[DatabaseManager] TaskIngest provenance:", JSON.stringify(ingestEvent.provenance));
      }
    }

    const store = await this._storePromise;
    console.log("[DatabaseManager] Got store, calling putEvents...");
    await store.database.putEvents(events);
    console.log("[DatabaseManager] putEvents completed");

    // Verify events were stored by listing tasks immediately (before sync)
    const tasksAfterPut = await store.database.listEntities<Task>({
      entityType: EntityType.Task,
      limit: 100,
    });
    console.log("[DatabaseManager] Tasks after putEvents (before sync):", tasksAfterPut.length);
    if (tasksAfterPut.length > 0) {
      console.log("[DatabaseManager] Sample task provenance:", JSON.stringify(tasksAfterPut[0].provenance));
    }

    this._startSync();
    // Wait for sync to complete so collections are immediately visible
    // If a sync was already in progress, _startSync set _pendingSync = true
    // We need to wait for both the current sync AND the pending sync
    while (this._syncPromise || this._pendingSync) {
      if (this._syncPromise) {
        await this._syncPromise;
      }
      // Small delay to allow pending sync to start
      if (this._pendingSync && !this._syncPromise) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    console.log("[DatabaseManager] recordEvents completed (sync done)");
  }

  async getURLForAttachmentID(
    attachmentID: AttachmentID,
  ): Promise<string | null> {
    const store = await this._storePromise;
    return store.attachmentStore.getURLForStoredAttachment(attachmentID);
  }

  // ============================================================================
  // Collection/Task Listing Methods
  // ============================================================================

  async listAllTasks(options?: {
    limit?: number;
    includeDeleted?: boolean;
  }): Promise<Task[]> {
    console.log("[DatabaseManager] listAllTasks called, syncPromise:", this._syncPromise !== null ? "pending" : "none");
    await this._syncPromise;
    console.log("[DatabaseManager] listAllTasks sync complete");
    const store = await this._storePromise;

    // Use a predicate that matches all tasks (far future timestamp)
    // This works around a potential bug in OrbitStore where queries without predicates return empty
    const farFutureTimestamp = Date.now() + (1000 * 60 * 60 * 24 * 365 * 100); // 100 years from now
    const tasks = await store.database.listEntities<Task>({
      entityType: EntityType.Task,
      limit: options?.limit ?? 2000,
      predicate: ["dueTimestampMillis", "<=", farFutureTimestamp],
    });
    console.log("[DatabaseManager] listAllTasks raw count:", tasks.length);

    // Log provenance info for first few tasks
    for (let i = 0; i < Math.min(3, tasks.length); i++) {
      console.log(`[DatabaseManager] Task ${i} id:`, tasks[i].id, "provenance:", JSON.stringify(tasks[i].provenance));
    }

    if (options?.includeDeleted) {
      return tasks;
    }
    const nonDeletedTasks = tasks.filter((task) => !task.isDeleted);
    console.log("[DatabaseManager] listAllTasks non-deleted count:", nonDeletedTasks.length);
    return nonDeletedTasks;
  }

  async listCollections(options?: {
    limitTasks?: number;
  }): Promise<CollectionSummary[]> {
    console.log("[DatabaseManager] listCollections called, waiting for sync...");
    const tasks = await this.listAllTasks({
      limit: options?.limitTasks ?? 2000,
      includeDeleted: false,
    });
    console.log(`[DatabaseManager] listCollections found ${tasks.length} tasks`);
    const summaries = buildCollectionSummaries(tasks);
    console.log(`[DatabaseManager] listCollections returning ${summaries.length} collections:`, summaries.map(s => s.title));
    return summaries;
  }

  async listTasksInCollection(
    collectionID: CollectionID,
    options?: { limit?: number; includeDeleted?: boolean },
  ): Promise<Task[]> {
    const allTasks = await this.listAllTasks({
      limit: options?.limit ?? 2000,
      includeDeleted: options?.includeDeleted,
    });

    return allTasks.filter(
      (task) => getCollectionIDForTask(task) === collectionID,
    );
  }

  async getTask(taskID: TaskID): Promise<Task | null> {
    await this._syncPromise;
    const store = await this._storePromise;

    // Note: The store API only supports dueTimestampMillis predicates,
    // so we need to list all tasks and find the one we want
    const tasks = await store.database.listEntities<Task>({
      entityType: EntityType.Task,
      limit: 2000,
    });

    return tasks.find((task) => task.id === taskID) ?? null;
  }

  // ============================================================================
  // Task Update Methods
  // ============================================================================

  async updateTaskSpec(taskID: TaskID, spec: TaskSpec): Promise<void> {
    const event: TaskUpdateSpecEvent = {
      type: EventType.TaskUpdateSpecEvent,
      id: generateUniqueID(),
      entityID: taskID,
      timestampMillis: Date.now(),
      spec,
    };
    await this.recordEvents([event]);
  }

  async updateTaskProvenance(
    taskID: TaskID,
    provenance: TaskProvenance | null,
  ): Promise<void> {
    const event: TaskUpdateProvenanceEvent = {
      type: EventType.TaskUpdateProvenanceEvent,
      id: generateUniqueID(),
      entityID: taskID,
      timestampMillis: Date.now(),
      provenance,
    };
    await this.recordEvents([event]);
  }

  async setTaskDeleted(taskID: TaskID, isDeleted: boolean): Promise<void> {
    const event: TaskUpdateDeletedEvent = {
      type: EventType.TaskUpdateDeleted,
      id: generateUniqueID(),
      entityID: taskID,
      timestampMillis: Date.now(),
      isDeleted,
    };
    await this.recordEvents([event]);
  }

  // ============================================================================
  // Collection Update Methods
  // ============================================================================

  async updateCollectionProvenance(
    collectionID: CollectionID,
    patch: { title?: string; colorPaletteName?: ColorPaletteName | null },
  ): Promise<void> {
    const tasks = await this.listTasksInCollection(collectionID);
    const events: TaskUpdateProvenanceEvent[] = [];

    for (const task of tasks) {
      const currentProvenance = task.provenance;
      const newProvenance: TaskProvenance = {
        identifier: collectionID,
        title: patch.title ?? currentProvenance?.title ?? collectionID,
        ...(currentProvenance?.url ? { url: currentProvenance.url } : {}),
        ...(patch.colorPaletteName !== undefined
          ? patch.colorPaletteName !== null
            ? { colorPaletteName: patch.colorPaletteName }
            : {}
          : currentProvenance?.colorPaletteName
            ? { colorPaletteName: currentProvenance.colorPaletteName }
            : {}),
      };

      events.push({
        type: EventType.TaskUpdateProvenanceEvent,
        id: generateUniqueID(),
        entityID: task.id,
        timestampMillis: Date.now(),
        provenance: newProvenance,
      });
    }

    if (events.length > 0) {
      await this.recordEvents(events);
    }
  }

  async moveCollectionToOrphans(collectionID: CollectionID): Promise<void> {
    const tasks = await this.listTasksInCollection(collectionID);
    const orphansProvenance = makeOrphansProvenance();
    const events: TaskUpdateProvenanceEvent[] = [];

    for (const task of tasks) {
      events.push({
        type: EventType.TaskUpdateProvenanceEvent,
        id: generateUniqueID(),
        entityID: task.id,
        timestampMillis: Date.now(),
        provenance: orphansProvenance,
      });
    }

    if (events.length > 0) {
      await this.recordEvents(events);
    }
  }

  async deleteAllTasksInCollection(collectionID: CollectionID): Promise<void> {
    const tasks = await this.listTasksInCollection(collectionID);
    const events: TaskUpdateDeletedEvent[] = [];

    for (const task of tasks) {
      events.push({
        type: EventType.TaskUpdateDeleted,
        id: generateUniqueID(),
        entityID: task.id,
        timestampMillis: Date.now(),
        isDeleted: true,
      });
    }

    if (events.length > 0) {
      await this.recordEvents(events);
    }
  }

  private _pendingSync = false;

  private _startSync() {
    // If a sync is already in progress, mark that another sync is pending
    if (this._syncPromise !== null) {
      console.info("[Sync] Sync already in progress, marking pending sync...");
      this._pendingSync = true;
      return;
    }

    this._pendingSync = false;
    console.info("[Sync] Starting bidirectional sync...");
    this._syncPromise = this._storePromise.then(async (store) => {
      try {
        // Log task count before sync
        const tasksBefore = await store.database.listEntities<Task>({
          entityType: EntityType.Task,
          limit: 100,
        });
        console.info("[Sync] Tasks before sync:", tasksBefore.length);

        // syncOrbitStore handles bidirectional sync:
        // 1. Push local changes to server
        // 2. Pull server changes to local
        // 3. Reconcile any remaining differences
        await syncOrbitStore({ source: store, destination: this._apiSyncAdapter });

        // Log task count after sync
        const tasksAfter = await store.database.listEntities<Task>({
          entityType: EntityType.Task,
          limit: 100,
        });
        console.info("[Sync] Tasks after sync:", tasksAfter.length);
        if (tasksAfter.length !== tasksBefore.length) {
          console.warn("[Sync] Task count changed during sync:", tasksBefore.length, "->", tasksAfter.length);
        }

        console.info("[Sync] Bidirectional sync completed.");
      } catch (error: unknown) {
        console.error("[Sync] Sync failed: ", error);
      } finally {
        this._syncPromise = null;
        // If another sync was requested while we were syncing, start it now
        if (this._pendingSync) {
          console.info("[Sync] Starting pending sync...");
          this._startSync();
        }
      }
    });
  }
}
